import { useState } from "react";

import OpenHoursEditor from "../OpenHoursEditor/OpenHoursEditor";
import {
  SENSOR_SETTING_LIMITS as LIMITS,
  WEEKDAYS,
  parseOpenHours,
  patchVenue,
  type OpenHours,
  type VenueSensorSettings,
  type VenuePlan,
  type VenueStatus,
  type VenueType,
} from "../../../Services/Admin/Venues";
import "./VenueEditor.css";

/**
 * Redigerar ett venue: synlighet på kartan, betalplan, stad, öppettider och
 * sensorinställningar. Sensorinställningarna ställs bara här — de gäller
 * alla sensorer på venuet från deras nästa heartbeat.
 *
 * Öppettiderna skickas som ett objekt, inte som en sträng — servern äger
 * hur de lagras. Råtexten från källan visas som referens när den finns,
 * eftersom en del strängar inte gick att tolka och behöver fyllas i för
 * hand.
 */
type Props = {
  venue: VenueType;
  /** Städer som redan finns, som förslag så att samma stad stavas likadant. */
  knownCities: string[];
  onSaved: (venue: VenueType) => void;
  onClose: () => void;
};

const STATUS_LABELS: Record<string, string> = {
  active: "Active — visible on the map",
  needs_review: "Needs review — hidden until approved",
  hidden: "Hidden — never shown",
};

export default function VenueEditor({ venue, knownCities, onSaved, onClose }: Props) {
  const [status, setStatus] = useState<VenueStatus>(venue.status);
  const [plan, setPlan] = useState<VenuePlan>(venue.plan ?? "free");
  const [city, setCity] = useState(venue.city ?? "");
  const [sensorSettings, setSensorSettings] = useState<VenueSensorSettings>(
    venue.sensorSettings
  );

  const setRecording = (field: "recordSeconds" | "intervalSeconds", value: number) =>
    setSensorSettings((s) => ({ ...s, [field]: value }));
  const setActivity = <K extends keyof VenueSensorSettings["activity"]>(
    field: K,
    value: VenueSensorSettings["activity"][K]
  ) => setSensorSettings((s) => ({ ...s, activity: { ...s.activity, [field]: value } }));
  const [hours, setHours] = useState<OpenHours>(() => parseOpenHours(venue.openHoursJson));

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasAnyHours = WEEKDAYS.some((d) => (hours[d] ?? []).length > 0);

  const save = async () => {
    // Servern kräver öppettider, och sensorn står still utan dem. Fångas
    // här också så att felet står vid fältet i stället för att komma
    // tillbaka som en kod från API:t.
    if (!hasAnyHours) {
      setError("Opening hours are required — the sensor stays off without them.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const res = await patchVenue(venue.id, {
        status,
        plan,
        city: city.trim(),
        openHours: hours,
        sensorSettings,
      });

      onSaved(res.venue);
      onClose();
    } catch (e: any) {
      setError(e?.message ?? "Could not save the venue");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="venue-editor">
      <div className="venue-editor-row">
        <label className="admin-field venue-editor-field">
          <span className="admin-label">Map visibility</span>
          <select value={status} onChange={(e) => setStatus(e.target.value as VenueStatus)}>
            {Object.keys(STATUS_LABELS).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </label>

        <label className="admin-field venue-editor-field">
          <span className="admin-label">Plan</span>
          <select value={plan} onChange={(e) => setPlan(e.target.value as VenuePlan)}>
            <option value="free">Free — grey pin, no live data</option>
            <option value="premium">Premium — live data when a sensor is on</option>
          </select>
        </label>
      </div>

      <label className="admin-field venue-editor-field">
        <span className="admin-label">City</span>
        <input
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder="Göteborg"
          list={`venue-editor-cities-${venue.id}`}
        />
        <datalist id={`venue-editor-cities-${venue.id}`}>
          {knownCities.map((c) => (
            <option key={c} value={c} />
          ))}
        </datalist>
      </label>

      {plan === "premium" && venue.sensors.length === 0 && (
        <p className="venue-editor-warning">
          Premium without a sensor still shows a grey pin. Connect a sensor below.
        </p>
      )}

      <OpenHoursEditor value={hours} onChange={setHours} />

      <fieldset className="venue-editor-sensors">
        <legend>Sensor settings</legend>
        <p className="venue-editor-raw">
          The only place these are set. They apply to{" "}
          {venue.sensors.length === 1 ? "the sensor" : `all ${venue.sensors.length} sensors`} on
          this venue from their next heartbeat.
        </p>

        <div className="venue-editor-row">
          <NumberField
            label="Recognition interval (s)"
            value={sensorSettings.intervalSeconds}
            range={LIMITS.intervalSeconds}
            onChange={(v) => setRecording("intervalSeconds", v)}
          />
          <NumberField
            label="Clip length (s)"
            value={sensorSettings.recordSeconds}
            range={LIMITS.recordSeconds}
            onChange={(v) => setRecording("recordSeconds", v)}
          />
        </div>
        <p className="venue-editor-raw">
          Beatspot Agent (software sensors) reports what the venue's computer
          is playing once per interval. Clip length and activity apply to
          hardware sensors only.
        </p>

        <label className="venue-editor-toggle">
          <input
            type="checkbox"
            checked={sensorSettings.activity.enabled}
            onChange={(e) => setActivity("enabled", e.target.checked)}
          />
          Measure activity (experimental)
        </label>

        {sensorSettings.activity.enabled && (
          <>
            <div className="venue-editor-row">
              {(["wifiEnabled", "bleEnabled", "audioEnabled"] as const).map((field) => (
                <label className="venue-editor-toggle" key={field}>
                  <input
                    type="checkbox"
                    checked={sensorSettings.activity[field]}
                    onChange={(e) => setActivity(field, e.target.checked)}
                  />
                  {{ wifiEnabled: "Wi-Fi", bleEnabled: "BLE", audioEnabled: "Audio" }[field]}
                </label>
              ))}
            </div>

            <div className="venue-editor-row">
              <NumberField
                label="Activity interval (s)"
                value={sensorSettings.activity.intervalSeconds}
                range={LIMITS.activityIntervalSeconds}
                onChange={(v) => setActivity("intervalSeconds", v)}
              />
              <NumberField
                label="Wi-Fi window (s)"
                value={sensorSettings.activity.wifiSampleSeconds}
                range={LIMITS.wifiSampleSeconds}
                onChange={(v) => setActivity("wifiSampleSeconds", v)}
              />
              <NumberField
                label="BLE window (s)"
                value={sensorSettings.activity.bleSampleSeconds}
                range={LIMITS.bleSampleSeconds}
                onChange={(v) => setActivity("bleSampleSeconds", v)}
              />
            </div>

            <div className="venue-editor-row">
              <NumberField
                label="Wi-Fi min RSSI"
                value={sensorSettings.activity.wifiMinRssi}
                range={LIMITS.minRssi}
                onChange={(v) => setActivity("wifiMinRssi", v)}
              />
              <NumberField
                label="BLE min RSSI"
                value={sensorSettings.activity.bleMinRssi}
                range={LIMITS.minRssi}
                onChange={(v) => setActivity("bleMinRssi", v)}
              />
            </div>
          </>
        )}
      </fieldset>

      {venue.openHoursRaw && (
        <p className="venue-editor-raw">
          From source: <code>{venue.openHoursRaw}</code>
        </p>
      )}

      {error && <p className="admin-error">{error}</p>}

      <div className="venue-editor-actions">
        <button
          type="button"
          className="admin-btn--primary"
          disabled={saving}
          onClick={save}
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
        <button type="button" disabled={saving} onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function NumberField({
  label,
  value,
  range,
  onChange,
}: {
  label: string;
  value: number;
  range: { min: number; max: number };
  onChange: (value: number) => void;
}) {
  return (
    <label className="admin-field venue-editor-field">
      <span className="admin-label">
        {label} <small>{range.min}–{range.max}</small>
      </span>
      <input
        type="number"
        min={range.min}
        max={range.max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}
